// FilterList.tsx - Updated version with sorting and filtering
import {
  Accordion,
  AccordionContent,
  AccordionListItem,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingSpinner,
  Separator,
  InfiniteScroll,
  TruncatedText,
  SearchInput,
} from '@ais-ui/ui-core';
import { CreateFilter } from './Mutations/filter/CreateFilter';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Filter,
  Group,
  useGetAllGroups,
  useGetFilters,
} from '@ais-ui/websockets';
import { filterToIcon } from './filterToIcon';
import { isNonEmpty } from '@ais-ui/utils';
import { useIsMobile } from '@ais-ui/ui-core';
import { v4 as uuidv4 } from 'uuid';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { DeleteFilter } from './Mutations/filter/DeleteFilter';
import { EditFilter } from './Mutations/filter/EditFilter';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { ApplyFilterButton } from './ApplyFilterButton';
import { ApplyOnStartupButton } from './ApplyOnStartupButton';
import { FilterSort } from './FilterSort';
import { FilterFilter } from './FilterFilter';
import { useFilterFilters } from './useFilterFilters';

interface FilterItemProps {
  filter: Filter;
}

const getGroupsFromIds = (filter: Filter, groups: Group[]): Group[] => {
  const filterGroups = filter.groups || [];

  return (
    groups?.filter(
      (group) =>
        filterGroups.includes(group.uuid) || filterGroups.includes(group.name),
    ) || []
  );
};

interface FilterGridItemProps {
  label: string;
  value: string | undefined | null;
}
const FilterGridItem = ({ label, value }: FilterGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow truncate">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

const FilterItem = ({ filter }: FilterItemProps) => {
  const { messages } = useLocalization();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [filterGroups, setFilterGroups] = useState<Group[]>([]);
  const { data: groups } = useGetAllGroups();

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onEdit = () => {
    setShowEditDialog(true);
  };

  useEffect(() => {
    if (groups) {
      setFilterGroups(getGroupsFromIds(filter, groups));
    }
  }, [groups, filter]);

  const FilterIcon = useMemo(() => filterToIcon(filter), [filter]);

  return (
    <AccordionListItem
      key={filter.uuid}
      value={filter.uuid ? filter.uuid : uuidv4()}
      className="pr-2"
      actionButtons={
        <>
          <ApplyOnStartupButton filter={filter} />
          <ApplyFilterButton filter={filter} />
          <Separator orientation="vertical" className="h-6 bg-border" />
        </>
      }
      triggerContent={
        <>
          <FilterIcon className="size-4 flex-shrink-0" />
          <div className="w-full">
            <TruncatedText
              text={filter.name}
              className="text-sm text-left font-medium group-hover:underline"
              maxWidth="180px"
            />
          </div>
        </>
      }
      kebabMenu={
        <FilterDropDownMenu
          filter={filter}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      }
    >
      <AccordionContent>
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <FilterGridItem
                label={messages.filtersItemOwnerLabel}
                value={filter.owner}
              />
              <FilterGridItem
                label={messages.filtersItemShareLabel}
                value={
                  filterGroups.length > 0
                    ? filterGroups.map((group) => group.name).join(', ')
                    : messages.filtersItemShareEmptyValue
                }
              />
            </div>
            <FilterGridItem
              label={messages.filtersItemDefinitionLabel}
              value={filter.filter}
            />
          </div>
        </div>
      </AccordionContent>
      {showDeleteDialog && (
        <DeleteFilter
          filter={filter}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
        />
      )}
      {showEditDialog && (
        <EditFilter
          showDialog={showEditDialog}
          setShowDialog={setShowEditDialog}
          filter={filter}
        />
      )}
    </AccordionListItem>
  );
};

const FilterDropDownMenu = ({
  filter,
  onDelete,
  onEdit,
}: {
  filter: Filter;
  onDelete: () => void;
  onEdit: (event: React.FormEvent) => void;
}) => {
  const { messages } = useLocalization();
  const isMobile = useIsMobile();

  // Don't show edit/delete dropdown on mobile (read-only)
  if (isMobile) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={`${filter.name}-dropdown-button`}
          size="icon"
          variant="ghost"
          className="size-7"
        >
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      {/* Both items open a dialog; returning focus to the trigger after the
          menu's exit animation would steal it from the dialog and dismiss
          any popover opened inside it. */}
      <DropdownMenuContent
        align="end"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onEdit}>
          {messages.genericEdit}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          {messages.genericDelete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const FilterList = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { data: filters, isLoading } = useGetFilters();
  const isMobile = useIsMobile();

  const {
    filteredAndSortedFilters,
    filterFilters,
    setFilterFilters,
    sortConfig,
    setSortConfig,
  } = useFilterFilters(filters || [], search);

  return (
    <>
      {/* Controls section - outside CardContent to avoid overflow-hidden */}
      <div className="px-6 pb-4">
        <CardDescription className="mb-4">
          {messages.filtersSubtitle}
        </CardDescription>
        <div className="flex gap-2">
          <SearchInput
            id="search"
            placeholder={messages.filtersSearchFilters}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            onClear={() => setSearch('')}
          />
          <FilterSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <FilterFilter
            filterFilters={filterFilters}
            setFilterFilters={setFilterFilters}
            filters={filters}
          />
        </div>
      </div>

      {/* Content section */}
      <CardContent className="flex-1 flex flex-col overflow-hidden pt-0">
        {!isLoading && filters?.length === 0 && (
          <NoItemsEmptyStateList entity="filter" srcImage="empty_list_bro.avif">
            <Button
              id="filters-create-button"
              onClick={() => setShowCreateDialog(true)}
              className="w-full h-10"
            >
              Create filter
            </Button>
          </NoItemsEmptyStateList>
        )}

        {isLoading && <LoadingSpinner />}

        {!isLoading && filters?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col min-h-0"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredAndSortedFilters}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg shadow-xs"
              renderItem={(value: Filter) => {
                return <FilterItem key={value.uuid} filter={value} />;
              }}
            />
          </Accordion>
        )}
      </CardContent>

      {/* Hide Create button on mobile (read-only) */}
      {!isMobile && (
        <CardFooter className="flex flex-grow-0 flex-col items-end">
          {filters?.length !== 0 && (
            <Button
              id="filters-create-button"
              onClick={() => setShowCreateDialog(true)}
            >
              {messages.genericCreate}
            </Button>
          )}

          {showCreateDialog && (
            <CreateFilter
              showDialog={showCreateDialog}
              setShowDialog={setShowCreateDialog}
            />
          )}
        </CardFooter>
      )}
    </>
  );
};
